import {
  Box,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead
} from '@mui/material';
import { StyledTableCell, StyledTableRow } from '@components/StyledTable';
import { ArrElement } from '@utils/types';
import {
  FactoronBizSearchType,
  FetchFactoronQuery,
  Maybe,
  useFetchFactoronQuery
} from '@generated/graphql';
import useQueryString from '@hooks/useQueryString';
import { useState } from 'react';
import { DetailDrawer } from '@components/DetailDrawer';

export type FactoryOnData = ArrElement<
  NonNullable<FetchFactoronQuery['fetchFactoron']['data']>['icxModelList']
>;

type FactoryOnParamsType = {
  page: string;
  q: string;
  searchType: Maybe<FactoronBizSearchType>;
};

const LIMIT = 14;

const FactoryOn = () => {
  const { onUpdateQueryString, query } = useQueryString<FactoryOnParamsType>();

  const [drawer, setDrawer] = useState<{
    open: boolean;
    data?: FactoryOnData;
  }>({
    open: false
  });
  const page = Number(query.page ?? 1);

  const { data, loading, error } = useFetchFactoronQuery({
    variables: {
      input: {
        keyword: query.q,
        limit: LIMIT,
        offset: (page - 1) * LIMIT,
        factoronBizSearchType: query.searchType
      }
    }
  });

  if (loading || error) {
    return null;
  }

  const handleOpenDetailPage = (
    id: string | null | undefined,
    data: FactoryOnData
  ) => {
    if (!id) {
      return;
    }

    setDrawer({ open: true, data });
  };

  const factoryOnList = data?.fetchFactoron.data?.icxModelList ?? [];
  const factoryOnTotalCount = data?.fetchFactoron.data?.count ?? 0;

  return (
    <>
      <TableContainer component={Paper} sx={{ minWidth: 800 }}>
        <Table>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>회사명</StyledTableCell>
              <StyledTableCell>공장대표주소(도로명)</StyledTableCell>
              <StyledTableCell>업종명</StyledTableCell>
              <StyledTableCell>관할조직명</StyledTableCell>
              <StyledTableCell>생산품</StyledTableCell>
              <StyledTableCell>공장크기</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {factoryOnList.map((factoryOnData) => (
              <StyledTableRow
                onClick={() =>
                  handleOpenDetailPage(factoryOnData.id, factoryOnData)
                }
                key={factoryOnData.id}
                sx={{
                  '&:hover': {
                    cursor: 'pointer',
                    backgroundColor: '#00000030'
                  }
                }}
              >
                <StyledTableCell>{factoryOnData.companyName}</StyledTableCell>
                <StyledTableCell>
                  {factoryOnData.factoryAddressRoadName}
                </StyledTableCell>
                <StyledTableCell>{factoryOnData.industryName}</StyledTableCell>
                <StyledTableCell>
                  {factoryOnData.jurisdictionOrganizationName}
                </StyledTableCell>
                <StyledTableCell>{factoryOnData.product}</StyledTableCell>
                <StyledTableCell>{factoryOnData.factorySize}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        padding={2}
        width={'100%'}
        display={'flex'}
        justifyContent={'center'}
      >
        <Pagination
          count={Math.ceil(factoryOnTotalCount / LIMIT)}
          page={page}
          color={'primary'}
          onChange={(e, value) => {
            onUpdateQueryString({ page: `${value}` });
          }}
        />
        <DetailDrawer
          open={drawer.open}
          onClose={() => setDrawer({ open: false })}
          detailInfo={drawer.data}
          tables={[
            {
              title: '기본 정보',
              table: DefaultTable
            }
          ]}
        />
      </Box>
    </>
  );
};

export default FactoryOn;

const DefaultTable = {
  industrialComplex: '단지명',
  companyName: '회사명',
  representativeName: '대표자명',
  birthOrRegistrationNumber: '생년윌일/법인등록번호 ',
  industrialCenterName: '지식산업센터명',
  factoryControlNumber: '공장관리',
  factoryAddressRoadName: '공장대표주소 (도로명)',
  factoryAddressNo: '공장대표주소 (지번)',
  companyRegistrationNumber: '사업자등록번호',
  representativeIndustryCode: '대표업종차수',
  representativeIndustryNumber: '대표업종번호',
  sectors: '업종번호',
  industryName: '업종명',
  jurisdictionOrganizationName: '관할조직명',
  jurisdictionOrganizationCode: '관할조직코드',
  region: '시도',
  city: '시군구',
  establishment: '설립구분',
  moveInType: '입주형태',
  classificationOfHoldings: '보유구분',
  applicationDate: '신청일자',
  firstApprovalDate: '최초승인일',
  approvalClassification: '승인구분',
  approvalDate: '승인일',
  firstRegistrationDate: '최초등록일',
  registrationCategory: '등록구분',
  registrationDate: '등록일',
  registrationNumber: '등록번호',
  changeReportDate: '변경신고일',
  businessType: '사업유형',
  contact: '전화번호',
  faxNumber: '팩스번호',
  maleEmployeeCount: '남종업원',
  femaleEmployeeCount: '여종업원',
  maleForeigner: '외국인(남)',
  femaleForeigner: '외국인(여)',
  employeesCount: '종업원수',
  product: '생산품',
  mainMaterial: '주원자재',
  businessStartDate: '사업시작일',
  factoryCompletionDate: '공장준공일',
  estimatedConstructionStartDate: '공사착공예정일',
  estimatedConstructionCompletionDate: '공사준공예정일',
  factorySize: '공장크기',
  useArea: '용도지역',
  landCategory: '지목',
  landArea: '용지면적',
  manufacturingFacilityArea: '제조시설면적',
  ancillaryFacilityArea: '부대시설면적',
  officeArea: '사무실면적',
  warehouseArea: '창고면적',
  horizontalProjectionArea: '수평투영면적',
  buildingArea: '건축면적',
  airQuality: '대기등급',
  waterQuality: '수질등급',
  isNoiseVibration: '소음/진동여부',
  livingWater: '생활용수',
  industrialWater: '산업용수',
  power: '전력',
  oil: '석유',
  gas: '가스',
  etcOil: '기타',
  equityMillion: '자기자본액(백만)',
  debtCapitalMillion: '타인자본액(백만)',
  foreignInvestmentRatio: '외국인투자비율',
  foreignInvestment: '외국인투자액',
  cancellationDate: '취소일',
  cancellationReason: '취소사유',
  representativeEmail: '대표자이메일',
  representativePhoneNumber: '대표자전화번호',
  factoryWebsite: '공장홈페이지',
  factoryZipCode: '공장우편번호',
  representativeZipCode: '대표자우편번호',
  representativeAddress: '대표자주소',
  reasonChange: '변경사유',
  factoryManagementNumberOld: '공장관리번호(구)',
  expirationPeriod: '유효기간',
  factoryCondition: '공장상태',
  freeEconomicZone: '경제자유구역'
};
